import React, { useState } from "react";
import { UploadOutlined } from "@ant-design/icons";
import { Button, message, Modal, Upload, UploadFile, UploadProps, Alert, Table } from "antd";
import type { TableProps } from "antd";
import { Info, Warning } from "assets/icons";
import { IResultCsvData } from "store/readers-tags/tags/tags-model";
import theme from "styles/theme";
import useTranslation from "utils/hooks/useTranslation";
import * as S from "./index.styled";

interface DataType {
  key: string;
  columns: string;
  typeField: string;
  upgradeable: string;
  required: string;
}

interface IDispatchProps {
  tagsImportCsvAsync: (...args) => void;
  tagsHideImportCsvModal: (...args) => void;
  tagsUpdateResultCsvData: (...args) => void;
  tagsUpdateImportCsvData: (...args) => void;
}

interface IPropsScreen {
  visibleImportCsvModal: boolean;
  resultCsvData?: IResultCsvData;
  isLoadingImportCsvModal: boolean;
}

export type IImportTag = IPropsScreen & IDispatchProps;

const ImportTag = ({
  resultCsvData,
  isLoadingImportCsvModal,
  visibleImportCsvModal,
  tagsHideImportCsvModal,
  tagsUpdateResultCsvData,
  tagsUpdateImportCsvData,
  tagsImportCsvAsync,
}: IImportTag) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const TOTAL_TAGS_SUCCESSFUL = useTranslation({ id: "total_tags_successful" });
  const TOTAL_TAGS_WITH_ERROR = useTranslation({ id: "total_tags_with_error" });
  const TAGS_WITH_PROBLEMS = useTranslation({ id: "tags_with_problems" });
  const TAGS_TAGS_DUPLICATES = useTranslation({ id: "total_tags_duplicates" });
  const TAGS_TAGS_PROCESSED = useTranslation({ id: "total_tags_processed" });
  const errorFormat = useTranslation({ id: "only_csv_files_are_allowed" });
  const FILE_FOR_IMPORT = useTranslation({ id: "file_for_import" });
  const CONCLUDE = useTranslation({ id: "conclude" });
  const CONFIRM = useTranslation({ id: "confirm" });
  const RESULT = useTranslation({ id: "result" });
  const SELECT_FILE = useTranslation({ id: "select_file" });
  const ATTENTION = useTranslation({ id: "attention" });
  const CANCEL = useTranslation({ id: "cancel" });
  const CSV_IMPORT = useTranslation({ id: "csv_import" });
  const SUBMITEED_FILE_DESCRIPTION = useTranslation({ id: "the_submitted_file_must_contain_the_fields_shown_below" });

  const totalNumberOfTagsProcessedSuccessfully = resultCsvData?.totalProcessed;
  const totalNumberOfTagsInsertedSuccessfully = resultCsvData?.totalInserted;
  const totalNumberOfTagsDuplicates = resultCsvData?.totalDuplicates;
  const totalNumberOfTagsProcessedWithError = resultCsvData?.totalErrors;
  const tagsErrors = resultCsvData?.tagsErrors;
  const returnedResult = totalNumberOfTagsProcessedSuccessfully > 0 || totalNumberOfTagsProcessedWithError > 0;

  const props: UploadProps = {
    maxCount: 1,
    accept: ".csv",
    beforeUpload: (file) => {
      const isCsv = file.type === "text/csv" || file.name.toLowerCase().endsWith(".csv");

      if (!isCsv) {
        message.error(errorFormat);
        return Upload.LIST_IGNORE;
      }
      tagsUpdateResultCsvData(null);
      setFileList([file]);
      tagsUpdateImportCsvData(file);
      return false;
    },
    onRemove: () => {
      setFileList([]);
      tagsUpdateImportCsvData([]);
      tagsUpdateResultCsvData(null);
    },
    fileList,
  };

  const columns: TableProps<DataType>["columns"] = [
    {
      title: useTranslation({ id: "columns" }),
      dataIndex: "columns",
      key: "columns",
    },
    {
      title: useTranslation({ id: "field_type" }),
      dataIndex: "typeField",
      key: "typeField",
    },
    {
      title: useTranslation({ id: "updatable" }),
      dataIndex: "upgradeable",
      key: "upgradeable",
    },
    {
      title: useTranslation({ id: "required" }),
      dataIndex: "required",
      key: "required",
    },
  ];

  const data: DataType[] = [
    {
      key: "1",
      columns: useTranslation({ id: "tag_number" }),
      typeField: useTranslation({ id: "numeric" }),
      upgradeable: useTranslation({ id: "no" }),
      required: useTranslation({ id: "yes" }),
    },
    {
      key: "2",
      columns: useTranslation({ id: "iev_number" }),
      typeField: useTranslation({ id: "alphanumeric" }),
      upgradeable: useTranslation({ id: "no" }),
      required: useTranslation({ id: "yes" }),
    },
    {
      key: "3",
      columns: useTranslation({ id: "unit" }),
      typeField: useTranslation({ id: "numeric" }),
      upgradeable: useTranslation({ id: "no" }),
      required: useTranslation({ id: "yes" }),
    },
  ];

  const tagsWithError = () => {
    return resultCsvData?.tagsErrors.map((data) => (
      <>
        {data} <br />
      </>
    ));
  };

  const resultsReturnedAfterUpload = (
    <>
      {TAGS_TAGS_PROCESSED}: {totalNumberOfTagsProcessedSuccessfully} <br />
      {TOTAL_TAGS_SUCCESSFUL}: {totalNumberOfTagsInsertedSuccessfully} <br />
      {TAGS_TAGS_DUPLICATES}: {totalNumberOfTagsDuplicates} <br />
      {TOTAL_TAGS_WITH_ERROR}: {totalNumberOfTagsProcessedWithError} <br />
      {tagsErrors?.length > 0 && (
        <>
          {TAGS_WITH_PROBLEMS}: <br />
          {tagsWithError()} <br />
        </>
      )}
    </>
  );

  const handleCloseImportCsvModal = () => {
    tagsHideImportCsvModal();
    setFileList([]);
  };

  return (
    <Modal
      title={CSV_IMPORT}
      centered
      maskClosable={false}
      confirmLoading={isLoadingImportCsvModal}
      okButtonProps={{ className: "disabledBtn", disabled: fileList.length === 0 && !returnedResult }}
      open={visibleImportCsvModal}
      onOk={() => {
        returnedResult ? handleCloseImportCsvModal() : tagsImportCsvAsync(fileList[0]);
      }}
      onCancel={() => handleCloseImportCsvModal()}
      okText={returnedResult ? CONCLUDE : CONFIRM}
      cancelText={CANCEL}
      bodyStyle={{ height: "auto" }}
      width={700}
    >
      <Alert message={ATTENTION} description={SUBMITEED_FILE_DESCRIPTION} type="warning" showIcon icon={<Warning width={24} height={24} fill={theme["@color-warning-400"]} />} />

      <S.DescriptionImportCsv>{FILE_FOR_IMPORT}</S.DescriptionImportCsv>

      <Upload {...props}>
        <Button icon={<UploadOutlined />}>{SELECT_FILE}</Button>
      </Upload>

      <Table<DataType> columns={columns} dataSource={data} pagination={false} style={{ marginTop: 20 }} />
      {returnedResult && (
        <Alert
          message={RESULT}
          description={<div style={{ maxHeight: 100, overflowY: "auto" }}>{resultsReturnedAfterUpload}</div>}
          type="info"
          showIcon
          icon={<Info width={24} height={24} />}
          style={{ marginTop: 20 }}
        />
      )}
    </Modal>
  );
};

export default ImportTag;
